import React, { useCallback, useEffect, useState } from "react";
import {
    AssignablePerson, GroupMember, ModuleDefinition, ModuleKey, PermissionGroup, PermissionSet,
    addGroupMember, createPermissionGroup, deletePermissionGroup, getAssignableStaff,
    getPermissionGroup, getPermissionGroups, getPermissionModules, removeGroupMember,
    updatePermissionGroup,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { can } from "../../utils/roles";
import { useConfirm } from "../common/ConfirmDialog";
import {
    FaLock, FaSyncAlt, FaPlus, FaTrash, FaUsers, FaExclamationTriangle, FaTimes,
} from "react-icons/fa";

/**
 * Permission groups.
 *
 * A group names a set of modules and what may be done in each; people are put
 * into groups; permissions are the union of the groups somebody is in.
 *
 * Super users and IT never appear here. Their authority is environment
 * configuration, so a group can neither add to it nor take it away — which is
 * what makes this screen safe to hand over: however badly a group is
 * configured, somebody can always undo it.
 */

const errorOf = (e: any, fallback: string) =>
    e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.error ?? fallback;

const GroupManager: React.FC = () => {
    const { profile } = useAuth();
    const confirm = useConfirm();

    const [modules, setModules] = useState<ModuleDefinition[]>([]);
    const [groups, setGroups] = useState<PermissionGroup[]>([]);
    const [selected, setSelected] = useState<PermissionGroup | null>(null);
    const [members, setMembers] = useState<GroupMember[]>([]);
    const [assignable, setAssignable] = useState<AssignablePerson[]>([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [note, setNote] = useState("");
    const [newName, setNewName] = useState("");
    const [addUid, setAddUid] = useState("");

    const allowed = can(profile, "groups", "view");
    const editable = can(profile, "groups", "manage");

    const load = useCallback(async () => {
        if (!allowed) return;
        setLoading(true);
        try {
            const [mods, list] = await Promise.all([getPermissionModules(), getPermissionGroups()]);
            setModules(mods);
            setGroups(list);
            setError("");
        } catch (e: any) {
            setError(errorOf(e, "Could not load the groups."));
        } finally {
            setLoading(false);
        }
    }, [allowed]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!allowed) return;
        getAssignableStaff().then(setAssignable).catch(() => setAssignable([]));
    }, [allowed]);

    const open = async (id: number) => {
        setError(""); setNote("");
        try {
            const { group, members: m } = await getPermissionGroup(id);
            setSelected(group);
            setMembers(m);
        } catch (e: any) {
            setError(errorOf(e, "Could not open that group."));
        }
    };

    const create = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setSaving(true); setError("");
        try {
            const group = await createPermissionGroup({ name: newName.trim() });
            setNewName("");
            await load();
            await open(group.id);
        } catch (e: any) {
            setError(errorOf(e, "That group could not be created."));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Save one checkbox.
     *
     * The whole matrix is sent because that is what the server stores — a
     * partial update would have to guess what the unlisted modules meant.
     */
    const setGrant = async (key: ModuleKey, action: "view" | "manage", on: boolean) => {
        if (!selected || !editable) return;
        const current = selected.permissions[key] ?? { view: false, manage: false };
        const next: PermissionSet = {
            ...selected.permissions,
            [key]: action === "view"
                // Withdrawing view withdraws manage too: an action on a screen
                // its holder cannot open is not a state worth keeping.
                ? { view: on, manage: on ? current.manage : false }
                // Granting manage grants view, matching the server.
                : { view: on ? true : current.view, manage: on },
        };

        setSelected({ ...selected, permissions: next });
        setSaving(true); setError("");
        try {
            const updated = await updatePermissionGroup(selected.id, { permissions: next });
            setSelected(updated);
            setGroups((prev) => prev.map((g) => (g.id === updated.id ? { ...g, permissions: updated.permissions } : g)));
        } catch (e: any) {
            setError(errorOf(e, "That change could not be saved."));
            await open(selected.id);   // put the screen back to what is stored
        } finally {
            setSaving(false);
        }
    };

    const add = async () => {
        if (!selected || !addUid) return;
        setSaving(true); setError(""); setNote("");
        try {
            const res = await addGroupMember(selected.id, addUid);
            setMembers(res.members);
            setAddUid("");
            if (res.note) setNote(res.note);
            await load();
        } catch (e: any) {
            setError(errorOf(e, "That person could not be added."));
        } finally {
            setSaving(false);
        }
    };

    const drop = async (m: GroupMember) => {
        if (!selected) return;
        const ok = await confirm({
            title: "Remove from this group?",
            message: `${m.displayName || m.email} will lose whatever "${selected.name}" grants, ` +
                `unless another group gives it to them.`,
            confirmLabel: "Remove",
            tone: "danger",
        });
        if (!ok) return;

        setSaving(true); setError("");
        try {
            setMembers(await removeGroupMember(selected.id, m.uid));
            await load();
        } catch (e: any) {
            setError(errorOf(e, "That person could not be removed."));
        } finally {
            setSaving(false);
        }
    };

    const destroy = async (group: PermissionGroup) => {
        const ok = await confirm({
            title: `Delete "${group.name}"?`,
            message: group.memberCount
                ? `${group.memberCount} ${group.memberCount === 1 ? "person" : "people"} will lose whatever ` +
                  `it grants. Their accounts are not affected.`
                : "This cannot be undone.",
            confirmLabel: "Delete group",
            tone: "danger",
        });
        if (!ok) return;

        setSaving(true); setError("");
        try {
            const res = await deletePermissionGroup(group.id);
            if (selected?.id === group.id) { setSelected(null); setMembers([]); }
            setNote(
                res.membersReleased > 0
                    ? `"${res.name}" deleted. ${res.membersReleased} ${res.membersReleased === 1 ? "person" : "people"} released.`
                    : `"${res.name}" deleted.`,
            );
            await load();
        } catch (e: any) {
            setError(errorOf(e, "That group could not be deleted."));
        } finally {
            setSaving(false);
        }
    };

    if (!allowed) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-sm text-center">
                    <FaLock className="mx-auto text-gray-400 mb-3" size={30} />
                    <h1 className="text-lg font-semibold text-gray-900 mb-1">Not available</h1>
                    <p className="text-sm text-gray-500">
                        You do not have permission to manage groups.
                    </p>
                </div>
            </div>
        );
    }

    const notInGroup = assignable.filter((p) => !members.some((m) => m.uid === p.uid));

    return (
        <div className="max-w-5xl mx-auto px-4 py-8">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Permission groups</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Decide what each part of the system a person can reach. Somebody in more than
                        one group gets everything their groups allow.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                >
                    <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}
            {note && (
                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">{note}</div>
            )}

            <div className="grid gap-6 md:grid-cols-[260px_1fr]">
                {/* ── The groups ── */}
                <div>
                    {editable && (
                        <form onSubmit={create} className="flex gap-2 mb-3">
                            <input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder="New group"
                                maxLength={60}
                                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                            />
                            <button
                                type="submit"
                                disabled={saving || !newName.trim()}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                            >
                                <FaPlus />
                            </button>
                        </form>
                    )}

                    <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden">
                        {loading && <div className="px-4 py-8 text-center text-sm text-gray-400">Loading…</div>}
                        {!loading && groups.length === 0 && (
                            <div className="px-4 py-8 text-center text-sm text-gray-400">
                                No groups yet.
                            </div>
                        )}
                        {groups.map((g) => (
                            <button
                                key={g.id}
                                onClick={() => open(g.id)}
                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${
                                    selected?.id === g.id ? "bg-gray-50" : ""
                                }`}
                            >
                                <div className="font-medium text-gray-900 text-sm">{g.name}</div>
                                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-400">
                                    <FaUsers className="text-[10px]" />
                                    {g.memberCount ?? 0} {g.memberCount === 1 ? "person" : "people"}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── One group ── */}
                <div>
                    {!selected ? (
                        <div className="rounded-xl border border-dashed border-gray-300 px-6 py-16 text-center text-sm text-gray-400">
                            Choose a group to see what it allows and who is in it.
                        </div>
                    ) : (
                        <>
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">{selected.name}</h2>
                                    {selected.description && (
                                        <p className="text-sm text-gray-500">{selected.description}</p>
                                    )}
                                </div>
                                {editable && (
                                    <button
                                        onClick={() => destroy(selected)}
                                        disabled={saving}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                    >
                                        <FaTrash /> Delete
                                    </button>
                                )}
                            </div>

                            {/* What it allows */}
                            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden mb-6">
                                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-900">What it allows</span>
                                    {saving && <span className="text-xs text-gray-400">Saving…</span>}
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="text-xs text-gray-500 bg-gray-50">
                                            <tr>
                                                <th className="text-left px-4 py-2 font-medium">Module</th>
                                                <th className="px-4 py-2 font-medium w-20">See</th>
                                                <th className="px-4 py-2 font-medium w-20">Change</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {modules.map((m) => {
                                                const grant = selected.permissions[m.key] ?? { view: false, manage: false };
                                                const readOnly = m.manageDescription.startsWith("Nothing extra");
                                                return (
                                                    <tr key={m.key}>
                                                        <td className="px-4 py-2.5">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-gray-900">{m.label}</span>
                                                                {m.governsAccess && (
                                                                    <span
                                                                        title="Granting this lets someone widen anybody's access, including their own"
                                                                        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800"
                                                                    >
                                                                        <FaExclamationTriangle className="text-[9px]" /> governs access
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-400">{m.description}</div>
                                                        </td>
                                                        <td className="px-4 py-2.5 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={grant.view || grant.manage}
                                                                disabled={!editable || saving}
                                                                onChange={(e) => setGrant(m.key, "view", e.target.checked)}
                                                                className="h-4 w-4 accent-gray-900 disabled:opacity-40"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-2.5 text-center">
                                                            {readOnly ? (
                                                                <span className="text-xs text-gray-300" title={m.manageDescription}>—</span>
                                                            ) : (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={grant.manage}
                                                                    disabled={!editable || saving}
                                                                    onChange={(e) => setGrant(m.key, "manage", e.target.checked)}
                                                                    title={m.manageDescription}
                                                                    className="h-4 w-4 accent-gray-900 disabled:opacity-40"
                                                                />
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Who is in it */}
                            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-900">
                                    Who is in it ({members.length})
                                </div>

                                {editable && (
                                    <div className="flex gap-2 px-4 py-3 border-b border-gray-100">
                                        <select
                                            value={addUid}
                                            onChange={(e) => setAddUid(e.target.value)}
                                            className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                        >
                                            <option value="">Add somebody…</option>
                                            {notInGroup.map((p) => (
                                                <option key={p.uid} value={p.uid}>
                                                    {p.displayName ? `${p.displayName} — ${p.email}` : p.email}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={add}
                                            disabled={saving || !addUid}
                                            className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                        >
                                            Add
                                        </button>
                                    </div>
                                )}

                                {members.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-sm text-gray-400">
                                        Nobody yet, so this group grants nothing to anyone.
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {members.map((m) => (
                                            <div key={m.uid} className="flex items-center justify-between gap-3 px-4 py-2.5">
                                                <div className="min-w-0">
                                                    <div className="text-sm text-gray-900">{m.displayName || m.email}</div>
                                                    {m.displayName && <div className="text-xs text-gray-400">{m.email}</div>}
                                                </div>
                                                {editable && (
                                                    <button
                                                        onClick={() => drop(m)}
                                                        disabled={saving}
                                                        className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                                                        title="Remove from this group"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <p className="mt-6 text-xs text-gray-500">
                Super users and IT are not listed and cannot be added — their access comes from the
                server's configuration, so no group can widen or narrow it. That is deliberate: it
                means a group can never lock everybody out of this screen.
            </p>
        </div>
    );
};

export default GroupManager;
